// Inline form for recording the actual answer of the current round
// against the captured round_id. Two input modes:
//   - lat/lng (precise-location modes)
//   - country name (country-only modes)
//
// The form submits to `record_truth` IPC, which writes a row in
// SQLite. The recorded values feed the Stats panel's distance /
// country-match calculations across rounds.
//
// No auto-OCR here — Phase 6 of the spec adds score-screen OCR
// to auto-record. This is the manual fallback.

import { useState } from "react";
import { recordTruth } from "../ipc/commands";
import { useStore } from "../state/store";
import { outputClassFor, type GameMode } from "../types/contract";

interface TruthRecorderProps {
  roundId: string;
  mode: GameMode;
}

export function TruthRecorder({ roundId, mode }: TruthRecorderProps) {
  const [open, setOpen] = useState(false);
  const [lat, setLat] = useState("");
  const [lng, setLng] = useState("");
  const [country, setCountry] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const showToast = useStore((s) => s.showToast);
  const isCountryOnly = outputClassFor(mode) === "country_only";

  const submit = async () => {
    setSubmitting(true);
    try {
      const latNum = lat.trim() ? Number(lat) : null;
      const lngNum = lng.trim() ? Number(lng) : null;
      // Reject NaN explicitly — the IPC accepts null for "not
      // recorded" but a NaN would be silently coerced to a number
      // that breaks distance math downstream.
      if (latNum !== null && Number.isNaN(latNum)) {
        showToast("Latitude isn't a number", "error");
        return;
      }
      if (lngNum !== null && Number.isNaN(lngNum)) {
        showToast("Longitude isn't a number", "error");
        return;
      }
      await recordTruth({
        roundId,
        lat: latNum,
        lng: lngNum,
        country: country.trim() || null,
      });
      showToast("Truth recorded", "ok");
      setOpen(false);
      setLat("");
      setLng("");
      setCountry("");
    } catch (e) {
      showToast(`Couldn't record truth: ${String(e)}`, "error");
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="text-xs px-2 py-1 rounded bg-white/5 hover:bg-white/10"
        title="Record the actual answer for this round to track AI accuracy"
      >
        🎯 Truth
      </button>
    );
  }

  return (
    <div
      data-testid="truth-recorder"
      className="flex flex-col gap-2 rounded border border-white/10 bg-white/5 p-2"
    >
      <div className="flex items-center gap-2">
        <span className="text-xs font-semibold">Record truth</span>
        <span className="ml-auto text-[10px] text-fg-muted">
          round {roundId.slice(0, 8)}
        </span>
        <button
          type="button"
          onClick={() => setOpen(false)}
          className="text-[10px] text-fg-muted hover:text-fg"
          aria-label="close truth recorder"
        >
          ✕
        </button>
      </div>
      {!isCountryOnly && (
        <div className="flex gap-2">
          <input
            type="text"
            inputMode="decimal"
            placeholder="Latitude"
            value={lat}
            onChange={(e) => setLat(e.target.value)}
            className="flex-1 rounded bg-white/10 border border-white/10 px-2 py-1 text-xs"
          />
          <input
            type="text"
            inputMode="decimal"
            placeholder="Longitude"
            value={lng}
            onChange={(e) => setLng(e.target.value)}
            className="flex-1 rounded bg-white/10 border border-white/10 px-2 py-1 text-xs"
          />
        </div>
      )}
      <input
        type="text"
        placeholder={isCountryOnly ? "Country (e.g. Sweden)" : "Country (optional)"}
        value={country}
        onChange={(e) => setCountry(e.target.value)}
        className="rounded bg-white/10 border border-white/10 px-2 py-1 text-xs"
      />
      <div className="flex gap-2">
        <button
          type="button"
          onClick={() => void submit()}
          disabled={submitting}
          className="flex-1 text-xs px-2 py-1 rounded bg-white/10 hover:bg-white/20 disabled:opacity-50"
        >
          {submitting ? "Saving…" : "Save"}
        </button>
      </div>
    </div>
  );
}
